import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Input, notification, Row, Select, Table } from "antd";
import Icon, { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { httpService } from "../../base/services/httpService.service";
import AuthService from "../../base/services/authentication.service";
import useRowSelection from "../../base/hooks/useRowSelection";
import { createQueryParams } from "../../helpers/queryParams";
import { formatDate } from "../../helpers/formatDate";
import { ModalTypesEnum, StatusCodesEnum, UserRolesEnum } from "../../utils/enums";
import { ArrowRightIcon } from "../../assets/icons/ArrowRightIcon";
import { DeleteComponent } from "../../pages/admin/workspace/components/DeleteComponent";
import Banner from "./Banner";
import AddNewTicket from "./AddNewTicket";
import ActionsIcons from "../../ui/ActionsIcons/ActionsIcons";

import "./Tickets.scss";

const { Option } = Select;

const Tickets = () => {
   const navigate = useNavigate();

   const { rowSelection, onRow, selectedRowKeys, setSelectedRowKeys } = useRowSelection();
   const [isLoading, setIsLoading] = useState(false);
   const [deleteModalOpen, setDeleteModalOpen] = useState("");
   const [addTicketModal, setAddTicketModal] = useState(false);
   const [tickets, setTickets] = useState([]);
   const [ticketTypes, setTicketTypes] = useState([]);
   const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 10,
      total: 0
   });
   const [filterValue, setFilterValue] = useState({
      searchInput: "",
      type: null
   });

   const fetchData = useCallback(
      (page = 1, pageSize = 10) => {
         try {
            setIsLoading(true);
            const queryParams = createQueryParams(filterValue);
            const params = new URLSearchParams({
               page: page,
               pageSize: pageSize
            });

            const additionalParams = new URLSearchParams(queryParams);
            for (const [key, value] of additionalParams) {
               params.append(key, value);
            }

            httpService.get(
               `/Tickets/getAllTickets?${params.toString()}`,
               (response) => {
                  const formattedData = response.data?.ticketResponses.map((item) => {
                     return {
                        ...item,
                        key: item.id,
                        createdAt: formatDate(item.createdAt)
                     };
                  });
                  setTickets(formattedData);
                  setPagination((prevState) => ({
                     ...prevState,
                     current: page,
                     pageSize: pageSize,
                     total: response?.data?.total
                  }));
                  setIsLoading(false);
               },
               (_err) => {
                  setIsLoading(false);
               }
            );
         } catch (error) {
            console.error("Error fetching tickets data", error);
         }
      },
      [filterValue]
   );

   const fetchDataTypes = useCallback(() => {
      try {
         httpService.get(
            "/Tickets/getTicketTypes",
            (response) => {
               const arrayOfObjects = Object.entries(response.data).map(([key, label]) => ({
                  label,
                  value: key
               }));
               setTicketTypes(arrayOfObjects);
            },
            (_err) => {}
         );
      } catch (error) {
         console.error("Error fetching tickets data", error);
      }
   }, []);

   useEffect(() => {
      fetchDataTypes();
   }, [fetchDataTypes]);

   useEffect(() => {
      if (filterValue.searchInput.trim() === "") {
         fetchData(pagination.current, pagination.pageSize);
         return;
      }

      const timeoutId = setTimeout(() => {
         fetchData(pagination.current, pagination.pageSize);
      }, 500);

      return () => clearTimeout(timeoutId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [filterValue]);

   const handlePagination = useCallback(
      (page, pageSize) => {
         fetchData(page, pageSize);
         setPagination((prevS) => ({ ...prevS, current: page, pageSize }));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [pagination]
   );

   const handleSearch = useCallback(
      (e) => {
         setPagination((prevS) => ({ ...prevS, current: 1 }));
         setFilterValue((prevS) => ({ ...prevS, searchInput: e.target.value }));
      },
      [setPagination, setFilterValue]
   );

   const onSelect = useCallback((value, key) => {
      setFilterValue((prevS) => ({ ...prevS, [key]: value }));
      setPagination((prevS) => ({ ...prevS, current: 1 }));
   }, []);

   const handleModal = () => {
      setAddTicketModal((prevState) => !prevState);
   };

   const handleBulkDelete = async (entity) => {
      await httpService.delete(
         "/Tickets/deleteTickets",
         selectedRowKeys,
         (res) => {
            notification.open({
               type: res.statusCode === StatusCodesEnum.OK ? "success" : "error",
               message: res.message || "Something went wrong! Please try again later."
            });
            setSelectedRowKeys([]);
            fetchData();
         },
         (_err) => {
            notification.error({ message: "Error deleting tickets, please try again later." });
         }
      );
   };

   const filterConfig = useMemo(
      () => ({
         key: "type",
         label: "Type",
         placeholder: "Filter by type",
         options: ticketTypes,
         multiple: false
      }),
      [ticketTypes]
   );

   const goToTicket = (record) => {
      navigate(`/tickets/${record.id}`);
   };

   const columns = useMemo(
      () => [
         {
            title: "Subject",
            dataIndex: "subject",
            width: 300
         },
         {
            title: "Type",
            dataIndex: "type"
         },
         {
            title: "Creation date",
            dataIndex: "createdAt"
         },
         {
            title: "Submitted by",
            dataIndex: "submittedBy"
         },
         {
            title: "",
            key: "actions",
            dataIndex: "actions",
            fixed: "right",
            width: 80,
            align: "center",
            render: (_, record) => (
               <ActionsIcons
                  actions={[
                     {
                        icon: <ArrowRightIcon />,
                        tooltip: "View",
                        onClick: () => goToTicket(record)
                     }
                  ]}
               />
            )
         }
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
   );

   return (
      <div className="tickets">
         <Banner></Banner>
         <div className="tickets__content">
            <div className="tickets__header">
               <h3 className="tickets__title">Ticket history list</h3>
               <div className="tickets__actions">
                  <Button
                     className="kapSurveyPrimaryButton"
                     onClick={() => setDeleteModalOpen(ModalTypesEnum.BULK)}
                     icon={
                        <Icon
                           component={() => (
                              <svg
                                 width="16"
                                 height="16"
                                 viewBox="0 0 16 16"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <path
                                    d="M0.884033 4.04796H14.5983M4.88403 4.04796V3.47653C4.88403 2.71877 5.18505 1.99204 5.72087 1.45622C6.25668 0.920404 6.98342 0.619385 7.74118 0.619385C8.49894 0.619385 9.22567 0.920404 9.76149 1.45622C10.2973 1.99204 10.5983 2.71877 10.5983 3.47653V4.04796M6.02689 7.4782V12.0514M9.45546 7.4782V12.0514M2.59832 4.04796H12.884V14.3337C12.884 14.6368 12.7636 14.9275 12.5493 15.1418C12.335 15.3561 12.0443 15.4765 11.7412 15.4765H3.74118C3.43807 15.4765 3.14738 15.3561 2.93305 15.1418C2.71873 14.9275 2.59832 14.6368 2.59832 14.3337V4.04796Z"
                                    stroke="#fff"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                 />
                              </svg>
                           )}
                        />
                     }
                     style={{
                        fontFamily: "Poppins",
                        fontSize: "14px"
                     }}
                     disabled={selectedRowKeys.length < 1}
                  >
                     Delete selected rows
                  </Button>
                  <Button
                     className="kapSurveyPrimaryButton"
                     icon={<PlusOutlined />}
                     disabled={isLoading}
                     loading={isLoading}
                     onClick={handleModal}
                  >
                     {isLoading ? <>Processing</> : "Add ticket"}
                  </Button>
               </div>
            </div>

            <Row justify="start" gutter={[16, 16]} className="search-filter">
               <Col xs={24} sm={24} md={10} lg={8} xl={8}>
                  <Input
                     placeholder="Search..."
                     onChange={handleSearch}
                     suffix={<SearchOutlined />}
                  />
               </Col>
               <Col key={filterConfig.key} xs={24} sm={24} md={10} lg={8} xl={8}>
                  <Select
                     size="medium"
                     mode={filterConfig.multiple ? "multiple" : null}
                     allowClear
                     showSearch
                     placeholder={filterConfig.placeholder}
                     optionFilterProp="children"
                     onChange={(value) => onSelect(value, filterConfig.key)}
                     filterOption={(input, option) => {
                        return (option?.children ?? "")
                           .toLowerCase()
                           .startsWith(input.toLowerCase());
                     }}
                     className="filter-select"
                     style={{
                        width: "100%"
                     }}
                     value={filterValue[filterConfig.key]}
                  >
                     {filterConfig.options.map((option) => (
                        <Option key={option.value} value={option.value}>
                           {option.label}
                        </Option>
                     ))}
                  </Select>
               </Col>
            </Row>

            <Table
               prefixCls="table-section"
               columns={columns}
               dataSource={tickets}
               rowSelection={
                  AuthService.isAuthorized([
                     UserRolesEnum.SuperAdmin,
                     UserRolesEnum.Organization,
                     UserRolesEnum.AdminOfOrganization,
                     UserRolesEnum.SeniorProjectManager,
                     UserRolesEnum.ProjectManager
                  ])
                     ? rowSelection
                     : undefined
               }
               onRow={onRow}
               scroll={{ x: "max-content" }}
               loading={isLoading}
               pagination={{
                  ...pagination,
                  onChange: (page, pageSize) => {
                     handlePagination(page, pageSize);
                  }
               }}
            />
         </div>
         {addTicketModal && (
            <AddNewTicket
               isModalOpen={addTicketModal}
               handleModal={handleModal}
               fetchData={fetchData}
            />
         )}

         {deleteModalOpen === ModalTypesEnum.BULK && selectedRowKeys.length > 0 && (
            <DeleteComponent
               open={deleteModalOpen === ModalTypesEnum.BULK && selectedRowKeys.length > 0}
               setOpen={() => setDeleteModalOpen("")}
               handleDelete={handleBulkDelete}
            />
         )}
      </div>
   );
};

export default Tickets;
